import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Snackbar,
  Alert
} from '@mui/material';
import { PersonAddOutlined } from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import EmployeeList from '../components/employees/EmployeeList';
import EmployeeFilters from '../components/employees/EmployeeFilters';
import EmployeeForm from '../components/employees/EmployeeForm';
import EmployeeDetailPanel from '../components/employees/EmployeeDetailPanel';
import DeactivateDialog from '../components/employees/DeactivateDialog';
import UserLinkDialog from '../components/employees/UserLinkDialog';
import { useEmployeesPaginated } from '../hooks/useEmployeesPaginated';
import { usePeopleAdmin } from '../context/PeopleAdminContext';

/**
 * Employees tab component for managing employee records with full CRUD operations
 * @param {Object} props - Component props
 * @param {Object} props.initialFilters - Initial filter values to apply
 * @returns {React.Component} Complete employee management interface
 */
function EmployeesTab({ initialFilters }) {
  const { clientId } = usePeopleAdmin();
  const navigate = useNavigate();

  // Component state
  const [formOpen, setFormOpen] = useState(false);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userLinkDialogOpen, setUserLinkDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Use paginated employees hook
  const {
    employees,
    filters,
    updateFilters,
    isLoading,
    createEmployee,
    updateEmployee,
    deactivateEmployee,
    reactivateEmployee,
    linkToUser,
    unlinkFromUser,
    isCreating,
    isUpdating,
    isDeactivating,
    isReactivating,
    isLinking,
    isUnlinking,
    refetch
  } = useEmployeesPaginated({ pageSize: 50 });

  // Apply initial filters when provided (only on mount with new filters)
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      updateFilters(initialFilters);
    }
  }, [JSON.stringify(initialFilters)]);

  // Form handlers
  const handleAddClick = () => {
    setSelectedEmployee(null);
    setFormOpen(true);
  };

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setDetailPanelOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedEmployee(null);
  };

  const handleFormSubmit = async (employeeData) => {
    try {
      // Create new employee (form is only used for adding now)
      await createEmployee(employeeData);
      showSnackbar('Employee created successfully', 'success');
      handleFormClose();
    } catch (error) {
      showSnackbar(error.message || 'Failed to save employee', 'error');
    }
  };

  const handleDetailPanelClose = () => {
    setDetailPanelOpen(false);
    setSelectedEmployee(null);
  };

  const handleDetailPanelUpdate = async (updateData) => {
    try {
      const updatedEmployee = await updateEmployee(updateData);
      showSnackbar('Employee updated successfully', 'success');
      
      // Update the selected employee with the returned data from the mutation
      if (updatedEmployee) {
        setSelectedEmployee(updatedEmployee);
      } else {
        // Fallback: merge the updates into the current selected employee
        if (selectedEmployee && updateData.id === selectedEmployee.id) {
          const mergedEmployee = {
            ...selectedEmployee,
            ...updateData.updates,
            // Ensure we keep fields that might not be in updates
            id: selectedEmployee.id,
            clientId: selectedEmployee.clientId,
            createdAt: selectedEmployee.createdAt,
            updatedAt: new Date().toISOString()
          };
          setSelectedEmployee(mergedEmployee);
        }
      }
      
      // The employees list will be refreshed by the query invalidation in the hook
    } catch (error) {
      showSnackbar(error.message || 'Failed to update employee', 'error');
      throw error; // Re-throw to let the detail panel handle it
    }
  };

  // Deactivation handlers
  const handleDeactivateClick = (employee) => {
    setSelectedEmployee(employee);
    setDeactivateDialogOpen(true);
  };

  const handleDeactivateClose = () => {
    setDeactivateDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleDeactivateConfirm = async (options) => {
    try {
      await deactivateEmployee(options);
      showSnackbar('Employee deactivated successfully', 'success');
      handleDeactivateClose();
    } catch (error) {
      showSnackbar(error.message || 'Failed to deactivate employee', 'error');
    }
  };

  // Reactivation handler
  const handleReactivate = async (employee) => {
    try {
      await reactivateEmployee(employee.id);
      showSnackbar('Employee reactivated successfully', 'success');
    } catch (error) {
      showSnackbar(error.message || 'Failed to reactivate employee', 'error');
    }
  };

  // User linking handlers
  const handleLinkUser = async (employee) => {
    setSelectedEmployee(employee);
    setUserLinkDialogOpen(true);
  };

  const handleUserLinkClose = () => {
    setUserLinkDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleUserLinkConfirm = async (selectedUser) => {
    if (!selectedUser || !selectedEmployee) {
      showSnackbar('Please select a user to link', 'error');
      return;
    }

    try {
      // Link the employee to the selected user
      await linkToUser({ employeeId: selectedEmployee.id, userId: selectedUser.id });
      showSnackbar(`Successfully linked ${selectedEmployee.firstName} ${selectedEmployee.lastName} to user ${selectedUser.email || selectedUser.username}`, 'success');
      handleUserLinkClose();
      // Refresh the employee list to show updated link status
      refetch();
    } catch (error) {
      showSnackbar(error.message || 'Failed to link user to employee', 'error');
    }
  };

  const handleUnlinkUser = async (employee) => {
    try {
      // Unlink both the employee from the user and the user from the employee
      // This maintains data integrity by clearing both relationships
      const userId = employee.userId;
      
      if (!userId) {
        showSnackbar('No user linked to this employee', 'warning');
        return;
      }
      
      // Unlink the employee from the user (sets Employee.userId to null)
      await unlinkFromUser(employee.id);
      
      // Also unlink the user from the employee (sets User.employeeId to null)
      // With the @index removed, this should now work
      const { unlinkUserFromEmployee } = await import('../services/userService');
      await unlinkUserFromEmployee(userId);
      
      showSnackbar('Successfully unlinked employee from RadixDash user', 'success');
      
      // Refresh the employee list to show updated link status
      refetch();
    } catch (error) {
      showSnackbar(error.message || 'Failed to unlink employee from user', 'error');
    }
  };

  const handleViewUser = (employee) => {
    // Navigate to User Admin page
    navigate('/userAdmin');
  };

  // Utility functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isProcessing = isCreating || isUpdating || isDeactivating || 
                       isReactivating || isLinking || isUnlinking;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Employees
        </Typography>
        <AdminButton
          variant="contained"
          startIcon={<PersonAddOutlined />}
          onClick={handleAddClick}
          disabled={isProcessing}
        >
          Add Employee
        </AdminButton>
      </Box>
      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <Box p={2}>
          <EmployeeFilters
            filters={filters}
            onFilterChange={updateFilters}
          />
        </Box>
      </Card>
      
      {/* Employee List */}
      <EmployeeList
        employees={employees}
        loading={isLoading}
        onEdit={handleEditClick}
        onDeactivate={handleDeactivateClick}
        onReactivate={handleReactivate}
        onLinkUser={handleLinkUser}
        onUnlinkUser={handleUnlinkUser}
        onViewUser={handleViewUser}
      />
      
      {/* Employee Form Dialog - Only for adding new employees */}
      <EmployeeForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        employee={null}
        clientId={clientId}
        loading={isCreating}
      />
      
      {/* Employee Detail Panel - For viewing/editing existing employees */}
      <EmployeeDetailPanel
        employee={selectedEmployee}
        open={detailPanelOpen}
        onClose={handleDetailPanelClose}
        onUpdate={handleDetailPanelUpdate}
        loading={isUpdating}
      />
      
      {/* Deactivate Confirmation Dialog */}
      <DeactivateDialog
        open={deactivateDialogOpen}
        onClose={handleDeactivateClose}
        onConfirm={handleDeactivateConfirm}
        employee={selectedEmployee}
        loading={isDeactivating}
      />
      
      {/* User Link Dialog */}
      <UserLinkDialog
        open={userLinkDialogOpen}
        onClose={handleUserLinkClose}
        onConfirm={handleUserLinkConfirm}
        employee={selectedEmployee}
        loading={isLinking}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

EmployeesTab.propTypes = {
  initialFilters: PropTypes.shape({
    search: PropTypes.string,
    isActive: PropTypes.bool,
    employmentType: PropTypes.string,
    hasUser: PropTypes.bool
  })
};

export default EmployeesTab;